import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const AppPreview = () => {
  const [activeScreenshot, setActiveScreenshot] = useState(0);

  const demoFeatures = [
    {
      title: "Dashboard Overview", 
      description: "Real-time overview of shelter operations with key metrics and quick actions",
      features: ["Current occupancy", "Staff on duty", "Daily statistics", "Quick access buttons"]
    },
    {
      title: "Staff Management",
      description: "Complete staff administration with time clock and scheduling", 
      features: ["Digital time clock", "Staff schedules", "Payroll tracking", "Role management"]
    },
    {
      title: "Resident Services",
      description: "HMIS-compliant intake process with digital forms and case management",
      features: ["HMIS compliance", "Case notes", "Room assignments", "Status tracking"]
    },
    {
      title: "Meal Tracking", 
      description: "Daily meal service tracking with detailed reporting and analytics",
      features: ["Service logging", "Demographic tracking", "Monthly reports", "Export options"]
    },
    {
      title: "Facility Operations",
      description: "Room management, maintenance requests, and operational tracking", 
      features: ["Room occupancy", "Maintenance tracking", "Chore management", "Reporting"]
    }
  ];

  return (
    <div className="py-12">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 text-center mb-16">
        <h1 className="text-4xl font-bold mb-4">
          See Shelter Care Connect in Action
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
          Explore screenshots and try our live demo to experience how Shelter Care Connect 
          can transform your shelter operations.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href="/demo" 
            target="_blank"
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold inline-block"
          >
            🎮 Try Live Demo
          </a>
          <Link 
            to="/beta" 
            className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold inline-block"
          >
            Apply for Beta Testing
          </Link>
        </div>
      </section>

      {/* Live Demo CTA */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 mb-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">Try the Live Demo</h2>
              <p className="text-blue-100 text-lg mb-6">
                Experience Shelter Care Connect with real functionality and sample data. 
                No registration required - just click and explore!
              </p>
              
              <div className="space-y-2 mb-6">
                <div className="flex items-center">
                  <span className="text-green-300 mr-2">✓</span>
                  <span>Full feature access</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-300 mr-2">✓</span>
                  <span>Sample shelter data included</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-300 mr-2">✓</span>
                  <span>No impact on production systems</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-300 mr-2">✓</span>
                  <span>Reset daily with fresh data</span>
                </div>
              </div>
              
              <a 
                href="/demo"
                target="_blank"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold inline-block"
              >
                Launch Demo Environment →
              </a>
            </div>
            
            <div className="text-center">
              <div className="bg-white/10 p-6 rounded-lg">
                <div className="text-4xl mb-4">🖥️</div>
                <h3 className="text-xl font-semibold mb-2">Demo Credentials</h3>
                <div className="text-blue-100">
                  <p>Username: <code className="bg-white/20 px-2 py-1 rounded">demo</code></p>
                  <p>Password: <code className="bg-white/20 px-2 py-1 rounded">demo123</code></p>
                </div>
                <p className="text-sm text-blue-200 mt-2">
                  Or browse without logging in to see public features
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Features */}
      <section className="max-w-7xl mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12">
          Features You Can Try in the Demo
        </h2>
        
        {/* Features You Can Try */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {demoFeatures.map((feature, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-lg border">
              <h3 className="text-xl font-bold mb-3 text-blue-800">
                {feature.title}
              </h3>
              <p className="text-gray-600 mb-4">
                {feature.description}
              </p>
              
              <h4 className="font-semibold text-gray-800 mb-2">Try These Features:</h4>
              <ul className="space-y-1 mb-6">
                {feature.features.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">✓</span>
                    <span className="text-gray-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              
              <a 
                href="/demo"
                target="_blank"
                className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-center"
              >
                Try Live Demo
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="bg-gray-50 py-16 mt-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Why Shelter Administrators Choose Us
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl mb-4">⏱️</div>
              <h3 className="text-xl font-semibold mb-2">Save 5+ Hours Weekly</h3>
              <p className="text-gray-600">
                Automated processes eliminate manual paperwork and streamline daily operations.
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-4xl mb-4">📋</div>
              <h3 className="text-xl font-semibold mb-2">HMIS Compliant</h3>
              <p className="text-gray-600">
                Built-in compliance ensures you meet all federal reporting requirements effortlessly.
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-4xl mb-4">📱</div>
              <h3 className="text-xl font-semibold mb-2">Mobile Ready</h3>
              <p className="text-gray-600">
                Access from any device - desktop, tablet, or phone. Perfect for staff mobility.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Transform Your Shelter Operations?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join our beta testing program and experience these features firsthand
          </p>
          
          <div className="space-x-4">
            <a 
              href="/demo"
              target="_blank"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold inline-block"
            >
              Try Live Demo
            </a>
            <Link 
              to="/beta" 
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold inline-block"
            >
              Apply for Beta
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AppPreview;